// ── useObserverMode (P12.2 + P12.2b) ──
// Resolves the current SuperAdmin observation state for a given surface,
// plus the optional Act-As escalation (typed-CONFIRM, 10-min window).
//
// Behavior:
//   • If role !== "super_admin"           → never in observer mode.
//   • If query param `observeAs=ID`       → seed sessionStorage and adopt.
//   • If sessionStorage has identity      → adopt and report observer mode.
//   • Otherwise                           → not in observer mode.
//
// Act-As (P12.2b):
//   • Only meaningful when SuperAdmin + observedId are present.
//   • Caller commits via `startActAs(reason)`. Returns true on success.
//   • Auto-expires after 10 minutes; on expiry the surface re-locks.
//   • Telemetry continues to tag `observer: true` (excluded from real KPIs).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRole } from "@/data/RoleContext";
import {
  ObserverKind,
  clearObserverIdentity,
  getObserverIdentity,
  setObserverIdentity,
} from "@/data/observerMode/observerSession";
import {
  ACT_AS_WINDOW_MS,
  clearActAsSession,
  getActAsRemainingMs,
  isActAsActive,
  startActAsSession,
} from "@/data/observerMode/actAsSession";

export interface ObserverModeState {
  /** True only when SuperAdmin is currently observing this surface. */
  isObserverMode: boolean;
  /** Resolved observed identity id (contractor or installer). */
  observedId: string | null;
  /** SuperAdmin gate — needed by surfaces to decide whether to render a picker. */
  isSuperAdmin: boolean;
  /** Exit observation: clears session for this surface and routes to Control Tower. */
  exit: () => void;
  /** True when SuperAdmin has escalated to write-enabled Act-As. */
  isActingAs: boolean;
  /** ms remaining in the active Act-As window, 0 when inactive. */
  actAsRemainingMs: number;
  /** Begin an Act-As window. Returns true on success. */
  startActAs: (reason: string) => boolean;
  /** End the Act-As window (re-locks the surface). Does NOT exit observation. */
  endActAs: () => void;
}

const QUERY_KEY = "observeAs";

function sanitizeObservedId(value: string | null): string | null {
  const v = String(value ?? "").trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === "unassigned" || lower === "-- pick --" || lower === "pick") return null;
  return v;
}

export function useObserverMode(kind: ObserverKind): ObserverModeState {
  const { role } = useRole();
  const isSuperAdmin = role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const rawQueryId = isSuperAdmin ? searchParams.get(QUERY_KEY) : null;
  const queryId = sanitizeObservedId(rawQueryId);

  // Seed sessionStorage when SuperAdmin lands with ?observeAs=...
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (rawQueryId && !queryId) {
      const next = new URLSearchParams(searchParams);
      next.delete(QUERY_KEY);
      setSearchParams(next, { replace: true });
      return;
    }
    if (queryId && queryId.trim()) {
      setObserverIdentity(kind, queryId.trim());
    }
  }, [isSuperAdmin, kind, queryId, rawQueryId, searchParams, setSearchParams]);

  const observedId = useMemo(() => {
    if (!isSuperAdmin) return null;
    if (queryId && queryId.trim()) return queryId.trim();
    return sanitizeObservedId(getObserverIdentity(kind));
  }, [isSuperAdmin, kind, queryId]);

  // ── Act-As ticking state ──
  const [actAsTick, setActAsTick] = useState(0);
  const isActingAs = isSuperAdmin && isActAsActive(kind, observedId);
  const actAsRemainingMs = isActingAs ? getActAsRemainingMs(kind) : 0;

  useEffect(() => {
    if (!isActingAs) return;
    const t = window.setInterval(() => setActAsTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
    // actAsTick excluded — interval is the driver
     
  }, [isActingAs, observedId]);

  // Auto-clear when window elapses naturally.
  useEffect(() => {
    if (isSuperAdmin && observedId && !isActingAs) {
      // ensure stale session is purged so next start is clean
      clearActAsSession(kind);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actAsTick]);

  const exit = useCallback(() => {
    clearObserverIdentity(kind);
    clearActAsSession(kind);
    const next = new URLSearchParams(searchParams);
    next.delete(QUERY_KEY);
    setSearchParams(next, { replace: true });
    navigate("/admin/control-tower");
  }, [kind, navigate, searchParams, setSearchParams]);

  const startActAs = useCallback(
    (reason: string) => {
      if (!isSuperAdmin || !observedId) return false;
      const sess = startActAsSession(kind, observedId, reason);
      if (!sess) return false;
      setActAsTick((n) => n + 1);
      return true;
    },
    [isSuperAdmin, kind, observedId],
  );

  const endActAs = useCallback(() => {
    clearActAsSession(kind);
    setActAsTick((n) => n + 1);
  }, [kind]);

  return {
    isObserverMode: isSuperAdmin && observedId != null,
    observedId,
    isSuperAdmin,
    exit,
    isActingAs,
    actAsRemainingMs,
    startActAs,
    endActAs,
  };
}

export { ACT_AS_WINDOW_MS };
